import { ref } from "vue";
import { defineStore } from "pinia";
import { PaymentMethod } from "@esenda/core-ui/interfaces";

/**
 * Store for managing payment-specific state, such as the current payment ID, available payment methods, selected provider, and details of the selected provider. This store is designed
 * to be used across the checkout process to maintain consistency of payment-related data and facilitate communication between different components and providers.
 * The store includes functions to set and update this information, and it persists the relevant data in session storage to maintain state across page reloads within the same session.
 * This allows for a seamless checkout experience for users, as their selected payment method and provider details will be retained even if they navigate away from the page or refresh it.
 */
export const usePaymentsStore = defineStore(
    "payments",
    () => {
        const paymentId = ref<string | null>(null);
        const paymentMethods = ref<PaymentMethod[] | null>(null);
        const selectedProvider = ref<string | null>(null);
        const selectedProviderDetails = ref<PaymentMethod | null>(null);
        const isPaymentInProgress = ref(false);

        function setPaymentId(id: string | null) {
            paymentId.value = id;
        }

        function setPaymentMethods(methods: PaymentMethod[] | null) {
            paymentMethods.value = methods;
        }

        function setSelectedProvider(provider: string | null) {
            selectedProvider.value = provider;
        }

        function setSelectedProviderDetails(details: PaymentMethod | null) {
            selectedProviderDetails.value = details;
        }

        function setIsPaymentInProgress(value: boolean) {
            isPaymentInProgress.value = value;
        }

        return {
            paymentId,
            paymentMethods,
            selectedProvider,
            selectedProviderDetails,
            isPaymentInProgress,
            setPaymentId,
            setPaymentMethods,
            setSelectedProvider,
            setSelectedProviderDetails,
            setIsPaymentInProgress,
        };
    },
    {
        persist: {
            pick: [
                "paymentId",
                "paymentMethods",
                "selectedProvider",
                "selectedProviderDetails",
            ],
            storage: sessionStorage,
        },
    },
);
